export type PaddlePrice = {
  net: number;
  gross: number;
  net_discount: number;
  gross_discount: number;
  net_after_discount: number;
  gross_after_discount: number;
  tax: number;
  tax_after_discount: number;
};

export interface PaddleCheckoutDiscountsItem {
  rank: number;
  code: string;
  type: 'percentage' | 'flat';
  description: string;
  gross_discount: number;
  net_discount: number;
}

export interface PaddleCheckoutCustomerItem {
  /**
   * 3 Char String
   * e.g. `EUR`, `USD`
   */
  currency: string;
  product_id: number;
  name: string;
  checkout_product_id: number;
  unit_price: PaddlePrice;
  discounts: Array<PaddleCheckoutDiscountsItem>;
  icon_url: string;
  tax_rate: number;
  allow_quantity: boolean;
  min_quantity: number;
  max_quantity: number;
  recurring?: {
    period: 'month' | 'year';
    unit_price: PaddlePrice;
  };
}

/**
 * Type for the object passed into the `eventCallback`
 * function, that can be passed to the
 * `Paddle.Checkout.Open(...)` call.
 */
export interface PaddleCheckoutData {
  id: string;
  coupon?: {
    coupon_code: string;
  };
  prices: {
    customer: {
      currency: string;
      total: number;
      total_tax: number;
      items: PaddleCheckoutCustomerItem[];
    };
  };
  /**
   * Only available for subscription products
   */
  recurring_prices?: {
    customer: {
      currency: string;
      total: number;
      total_tax: number;
      items: PaddleCheckoutCustomerItem[];
    };
    interval?: {
      length: number;
      type: 'year' | 'month';
    };
  };
}

export interface Checkout {
  id: number | string;
}

export interface IPaddleCheckoutResponseData {
  checkout: Checkout;
}

/**
 * Returned by a call to the global
 * paddle Objects `Product.Prices` function
 */
export interface PaddleApiPrice {
  /**
   * E.g. `€3.99`
   */
  net: string;
  gross: string;
  tax: string;
  tax_included: boolean;
}

export type PriceInfo = {
  productId?: number | string;
  country: string;
  quantity: number;
  price: PaddleApiPrice;

  recurring?: {
    price: PaddlePrice;
    subscription: {
      length: number;
      trial_days: number;
      type: string;
    };
  };
};

export interface IPaddleProduct {
  Prices: (
    product_id: number | string,
    quantity: number,
    callback: (data: PriceInfo) => void
  ) => void;
}

/**
 * Paddle OrderDetailsOrder object, contains
 * all relevant information returned by the paddle API.
 */
export interface IPaddleOrderDetailsOrder {
  coupon_code: string;
  currency: string;
  customer: object;
  customer_success_redirect_url: string;
  formatted_tax: string;
  formatted_total: string;
  has_locker: boolean;
  is_subscription: boolean;
  order_id: number;
  quantity: number;
  receipt_url: string;
  total: string;
  total_tax: string;
}

export interface IPaddleOrderResponseData {
  state: 'incomplete' | 'processing' | 'processed';
  order: IPaddleOrderDetailsOrder;
}

export interface IPaddleOrder {
  details: (
    checkout_id: string | number,
    callback: (order_data: IPaddleOrderResponseData) => void
  ) => void;
}

export interface IPaddleSpinner {
  show: () => void;
  hide: () => void;
}

export interface PaddleProductData {
  id: number;
  name: string;
  quantity: number;
}

export interface IPaddleEventData {
  country?: string;
  postcode?: string;
  product?: PaddleProductData;
  checkout?: PaddleCheckoutData;
}

export enum PaddleEvents {
  CHECKOUT_LOGIN = 'Checkout.Login',
  CHECKOUT_LOCATION_SUBMIT = 'Checkout.Location.Submit',
  CHECKOUT_PAYMENT_SELECTED = 'Checkout.PaymentMethodSelected',
  CHECKOUT_PAYMENT_COMPLETE = 'Checkout.PaymentComplete',
  CHECKOUT_USER_SUBSCRIBED = 'Checkout.User.Subscribed',
  CHECKOUT_COMPLETE = 'Checkout.Complete',
  CHECKOUT_LOADED = 'Checkout.Loaded',
  CHECKOUT_COUPON_APPLIED = 'Checkout.Coupon.Applied',
}

export interface IPaddleEventCallbackData {
  event: PaddleEvents;
  eventData: IPaddleEventData;
}

export interface IPaddleSetupConfig {
  vendor: number;
  eventCallback: (data: IPaddleEventCallbackData) => void;
}

export interface IPaddleCheckoutOpenConfig {
  product?: number | string;
  allowQuantity?: 'true' | 'false' | boolean;
  /**
   * Whether the user can change their email once on the checkout.
   *
   * Default: `false`
   */
  disableLogout?: boolean;
  /**
   * Whether the user can see the option to add a tax or VAT number.
   *
   * Default: `false`
   */
  hideTaxLink?: 'true' | 'false' | boolean;
  /**
   * Custom key-value data to include with this checkout.
   * Must be valid JSON and contain at least one key.
   *
   * Limited to 255 bytes.
   */
  customData?: object;
  quantity?: number;
  email?: string;
  /**
   * String of metadata to include with the checkout.
   * Sent with webhooks associated with the order.
   *
   * `<= 1000 characters`
   */
  passthrough?: string | object;
  title?: string;
  message?: string;
  postcode?: string;
  locale?: string;
  referring_domain?: string;
  displayModeTheme?: 'light' | 'dark';
  /**
   * Prefill a coupon code on the checkout.
   *
   * >=5 characters` `<=300 characters`
   */
  coupon?: string;
  country?: string;
  /**
   * Class name of the `<div>` element where
   * checkout should be rendered.
   */
  frameTarget?: string;
  /**
   * Height in pixels of the `<div>` on load.
   *
   * ! Do not include `px` !
   *
   * Recommended: `450`
   */
  frameInitialHeight?: number;
  /**
   * Styles to apply to the checkout `<div>`.
   *
   * `min-width` must be set to `286px` or above with checkout padding off;
   * `312px` with checkout padding on.
   *
   * Use `frameInitialHeight` to set height.
   *
   * Example: `min-width: 286px;background-color:transparent;`
   */
  frameStyle?: string;
  marketingConsent?: '0' | '1';
  /**
   * Display mode the checkout should use,
   * default ist 'overlay'
   */
  method?: 'inline' | 'overlay' | 'sdk';
  /**
   * URL to redirect to on checkout completion.
   */
  success?: string;
  /**
   * Name of a Javascript function (or function reference) to call on checkout completion.
   */
  successCallback?: string | ((data: IPaddleCheckoutResponseData) => void);
  /**
   * Name of a Javascript function to call on checkout close.
   */
  closeCallback?: string | (() => void);
  /**
   * Open the checkout with a custom pay link, cancel URL or payment method update URL.
   */
  override?: string;
}

export interface IPaddle {
  Setup: (data: IPaddleSetupConfig) => void;
  Product: IPaddleProduct;
  Checkout: {
    open: (data: IPaddleCheckoutOpenConfig) => void;
  };
  Order: IPaddleOrder;
  Spinner: IPaddleSpinner;
}
